import { cva, VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';

interface DrawerProps extends VariantProps<typeof drawerStyles> {
  dismiss: () => void;
  show: boolean;
  children: React.ReactNode;
  side?: 'left' | 'right';
}

const drawerStyles = cva(
  'fixed bg-brand-white h-screen w-full max-w-[80vw] sm:max-w-[42rem] overflow-clip',
  {
    variants: {
      side: {
        left: 'left-0',
        right: 'right-0',
      },
      top: {
        0: 'top-0',
        inherit: 'top-inherit',
      },
    },
    defaultVariants: {
      side: 'left',
      top: 0,
    },
  }
);

// Body scroll-lock is reference-counted across every mounted drawer. Each drawer
// used to snapshot and restore body overflow independently, so closing two
// drawers in a non-LIFO order (filter, then cart) had the second close restore
// the *locked* snapshot the first took — leaving the page scrollable behind an
// open drawer, or locked until reload. One shared counter fixes it: the body is
// unlocked only when the last drawer closes, and the pre-lock styles are
// captured once, on the 0→1 transition.
let lockCount = 0;
let savedOverflow: string | null = null;
let savedPaddingRight: string | null = null;

function lockBodyScroll() {
  const { body } = document;
  if (lockCount === 0) {
    // Width the scrollbar is occupying right now — this site styles it to 1rem
    // (10px at the 10px rem root). Zero when the page doesn't scroll, or on
    // platforms with overlay scrollbars.
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    savedOverflow = body.style.overflow;
    savedPaddingRight = body.style.paddingRight;

    body.style.overflow = 'hidden';

    // Locking scroll removes the scrollbar, which widens the viewport and
    // re-centres every `mx-auto max-w-default` container — the whole page
    // jumps sideways as the drawer opens. Replace the scrollbar's width with
    // padding so the content box keeps its size.
    if (scrollbarWidth > 0) {
      const currentPaddingRight =
        parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
  }
  lockCount += 1;
}

function unlockBodyScroll() {
  if (lockCount === 0) return;
  lockCount -= 1;
  if (lockCount > 0) return;

  const { body } = document;
  // Restore exactly what was there rather than assuming 'auto'. removeProperty
  // rather than assigning '' so an empty previous value really clears the
  // declaration (jsdom ignores `style.paddingRight = ''`).
  if (savedOverflow) body.style.overflow = savedOverflow;
  else body.style.removeProperty('overflow');

  if (savedPaddingRight) body.style.paddingRight = savedPaddingRight;
  else body.style.removeProperty('padding-right');

  savedOverflow = null;
  savedPaddingRight = null;
}

const BaseDrawer = ({ show, dismiss, children, side, top }: DrawerProps) => {
  useEffect(() => {
    if (!show) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [show]);
  const topStyle = top === 0 ? 'top-0' : 'top-inherit';
  const xOffset = side === 'right' ? '+100%' : '-100%';
  return (
    // z-overlay (300) sits above the sticky header (z-sticky, 200). This was
    // z-[60], which worked only while the header was z-50 — the header rebuild
    // raised it to 200 and started painting over every drawer.
    <div className="fixed z-overlay">
      <AnimatePresence>
        {show && (
          <motion.div
            key="drawer"
            //   slide in from left
            initial={{ x: xOffset }}
            animate={{ x: 0 }}
            //   slide out to left
            exit={{ x: xOffset }}
            transition={{ duration: 0.5 }}
            className={`${drawerStyles({ side, top })} z-10`}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
      {/* dimmer overlay */}
      <AnimatePresence>
        {show && (
          <motion.div
            className={`fixed left-0 top-0 h-full w-full bg-brand-black ${topStyle}`}
            onClick={() => {
              dismiss();
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BaseDrawer;
